class PasCinematicHero extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    if (!this.slides.length) return;
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.duration = Number(this.dataset.duration) || 8000;
    this.autoplay = this.dataset.autoplay === 'true';
    this.playing = this.autoplay;
    this.progress = 0;
    this.lastTime = 0;
    this.inViewport = true;
    this.touchX = null;
    this.reducedQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = matchMedia('(max-width: 767px)');
    this.live = this.querySelector('[data-hero-live]');
    this.pagination = this.querySelector('[data-hero-pagination]');
    this.toggle = this.querySelector('[data-hero-toggle]');
    this.buildPagination();
    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.toggle?.addEventListener('click', () => { this.playing = !this.playing; this.updateToggle(); this.syncVideos(); });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
    });
    this.addEventListener('touchstart', (event) => { this.touchX = event.touches[0]?.clientX ?? null; }, { passive: true });
    this.addEventListener('touchend', (event) => {
      const end = event.changedTouches[0]?.clientX;
      if (this.touchX != null && end != null && Math.abs(end - this.touchX) > 48) this.go(this.index + (end < this.touchX ? 1 : -1));
      this.touchX = null;
    }, { passive: true });
    this.onVisibility = () => this.syncVideos();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.reducedQuery.addEventListener('change', () => { this.progress = 0; this.syncVideos(); });
    this.mobileQuery.addEventListener('change', () => this.updatePosters());
    this.observer = new IntersectionObserver(([entry]) => { this.inViewport = entry.isIntersecting; this.syncVideos(); }, { threshold: .12 });
    this.observer.observe(this);
    this.update(true);
    this.raf = requestAnimationFrame((time) => this.tick(time));
    document.addEventListener('shopify:block:select', this.onBlockSelect = (event) => {
      const target = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
      if (target >= 0) { this.playing = false; this.go(target); }
    });
  }
  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }
  get running() { return this.playing && !this.reducedQuery.matches && !document.hidden && this.inViewport; }
  buildPagination() {
    if (!this.pagination) return;
    this.pagination.replaceChildren();
    this.slides.forEach((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      const eyebrow = slide.querySelector('.pas-hero__eyebrow')?.textContent?.trim() || `Slide ${index + 1}`;
      button.setAttribute('aria-label', `Go to slide ${index + 1}: ${eyebrow}`);
      button.innerHTML = '<span></span>';
      button.addEventListener('click', () => this.go(index));
      this.pagination.append(button);
    });
  }
  go(next) {
    this.index = (next % this.slides.length + this.slides.length) % this.slides.length;
    this.progress = 0;
    this.lastTime = performance.now();
    this.update();
  }
  update(initial = false) {
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      if ('inert' in slide) slide.inert = !active;
      slide.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach((element) => {
        if (!active) { if (!element.hasAttribute('data-pas-tabindex')) element.setAttribute('data-pas-tabindex', element.getAttribute('tabindex') ?? ''); element.setAttribute('tabindex', '-1'); }
        else if (element.hasAttribute('data-pas-tabindex')) { const old = element.getAttribute('data-pas-tabindex'); old ? element.setAttribute('tabindex', old) : element.removeAttribute('tabindex'); element.removeAttribute('data-pas-tabindex'); }
      });
    });
    this.hydrate(this.index);
    this.hydrate((this.index + 1) % this.slides.length);
    this.updatePosters();
    this.updateToggle();
    this.updateProgress();
    this.syncVideos();
    const eyebrow = this.slides[this.index].querySelector('.pas-hero__eyebrow')?.textContent?.trim() || '';
    if (this.live && !initial) this.live.textContent = `Slide ${this.index + 1} of ${this.slides.length} — ${eyebrow}`;
  }
  hydrate(index) {
    if (this.reducedQuery.matches) return;
    const video = this.slides[index]?.querySelector('[data-hero-video]');
    if (!video || video.dataset.loaded) return;
    video.dataset.loaded = 'true';
    video.querySelectorAll('source[data-src]').forEach((source) => { source.src = source.dataset.src; });
    video.addEventListener('canplay', () => video.classList.add('is-ready'), { once: true });
    video.load();
  }
  updatePosters() {
    this.querySelectorAll('[data-hero-video]').forEach((video) => { video.poster = this.mobileQuery.matches ? video.dataset.mobilePoster : video.dataset.desktopPoster; });
  }
  syncVideos() {
    this.slides.forEach((slide, index) => {
      const video = slide.querySelector('[data-hero-video]');
      if (!video) return;
      if (index === this.index && this.running && video.dataset.loaded) video.play().catch(() => {});
      else { video.pause(); if (index !== this.index) video.currentTime = 0; }
    });
  }
  updateToggle() {
    if (!this.toggle) return;
    const paused = !this.playing;
    this.toggle.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
    this.toggle.querySelector('[data-play]').hidden = !paused;
    this.toggle.querySelector('[data-pause]').hidden = paused;
  }
  updateProgress() {
    [...(this.pagination?.children || [])].forEach((button, index) => {
      button.setAttribute('aria-current', index === this.index ? 'true' : 'false');
      button.firstElementChild.style.width = index < this.index ? '100%' : index === this.index ? `${this.progress * 100}%` : '0%';
    });
    const elapsed = this.progress * this.duration / 1000;
    this.slides[this.index]?.querySelectorAll('[data-window-in]').forEach((element) => {
      const visible = elapsed >= Number(element.dataset.windowIn) && elapsed < Number(element.dataset.windowOut);
      element.classList.toggle('is-visible', visible);
    });
    this.slides.forEach((slide, index) => { if (index !== this.index) slide.querySelectorAll('.is-visible').forEach((item) => item.classList.remove('is-visible')); });
    this.slides[this.index]?.querySelectorAll('.pas-hero-warmth i').forEach((line, index) => { line.style.transform = `translateY(${Math.sin(elapsed * .9 + index) * 6}px) scaleX(${.9 + index * .03})`; });
  }
  tick(time) {
    if (!this.lastTime) this.lastTime = time;
    const delta = Math.min(time - this.lastTime, 100);
    this.lastTime = time;
    if (this.running) {
      this.progress += delta / this.duration;
      if (this.progress >= 1) this.go(this.index + 1);
      else this.updateProgress();
    }
    this.raf = requestAnimationFrame((nextTime) => this.tick(nextTime));
  }
}
if (!customElements.get('pas-cinematic-hero')) customElements.define('pas-cinematic-hero', PasCinematicHero);
